import React from 'react';
import { Placeholder, Responsive } from 'vendors/semantic-ui';

import { configUI } from 'config';

import { useHover } from 'modules/shared/hooks';
import { PlaceholderSize } from 'modules/shared/types';
import { readCMSValue, buildCMSStyles, getDefault } from 'modules/shared-cms/utils';
import { CMSImageContainer } from 'modules/shared-cms/components';
import { ClickableContainer } from 'modules/shared-ui/components';

import {
    CMSContainerField,
    Justification,
    JustifyProps,
    BackgroundColorProps,
    ContentPositionProps,
    WidthProps,
    Width,
} from 'modules/shared-cms/cms-base-type';
import { TitleBlocProps } from './title-bloc-type';
import { LegendBloc, LegendBlocLoader } from './legend-bloc';

import styles from './title-bloc-component.module.css';

interface WrapperContenuProps {
    color?: string | CMSContainerField<string>;
    justification?: Justification | CMSContainerField<Justification>;
    width?: Width | CMSContainerField<Width>;
}

const OuterWrapper: React.FC<ContentPositionProps> = props => {
    const { children } = props;
    return (
        <div className={styles.outerWrapper} style={buildCMSStyles(props)}>
            {children}
        </div>
    );
};

const WrapperContenu: React.FC<WrapperContenuProps> = props => {
    const { children } = props;
    return (
        <div
            className={styles['wrapper-contenu']}
            style={buildCMSStyles<BackgroundColorProps & JustifyProps & WidthProps>(props)}
        >
            {children}
        </div>
    );
};

export const convertPlaceholderSize = (size?: PlaceholderSize, compactResponsive = false) => {
    if (size === 'full') {
        return '100vh';
    }

    if (window.screen.width >= configUI.responsive.largeScreen.minWidth) {
        if (size === 'large') {
            return '550px';
        }

        return '400px';
    }

    if (window.screen.width >= configUI.responsive.computer.minWidth) {
        if (size === 'large') {
            return '362px';
        }

        return '273px';
    }

    if (size === 'large') {
        if (compactResponsive) {
            return '222px';
        }

        return '355px';
    }

    if (compactResponsive) {
        return '123px';
    }

    return '222px';
};

export const TitleBloc: React.FC<TitleBlocProps> = React.memo((props: TitleBlocProps) => {
    const { bind } = useHover();

    const {
        width = readCMSValue(props.width),
        justification = readCMSValue(props.justification),
        horizontalPosition = readCMSValue(props.horizontalPosition),
        verticalPosition = readCMSValue(props.verticalPosition),
        picture = getDefault(readCMSValue(props.picture), []),
        ctaLink = readCMSValue(props.ctaLink),
        ctaText2 = readCMSValue(props.ctaText2),
        color = readCMSValue(props.color),
        isChild = props.isChild || false,
        blockResponsive = props.blockResponsive || false,
        fullwidth = props.fullWidth || props.blockResponsive || false,
        legendBlocProps = {
            ...props,
            disableColorChange: props.blockResponsive,
        },
        placeHolderSize = convertPlaceholderSize(props.placeholderSize, props.compactResponsive),
    } = {};

    return (
        <div
            className={`${styles.rootContainer} ${isChild ? `${styles.isChild}` : ''} ${fullwidth ? 'full-width' : ''}`}
        >
            <ClickableContainer
                link={ctaText2 ? undefined : ctaLink}
                onHover={bind}
                innerClassName={`${styles.background} `}
            >
                <CMSImageContainer
                    picture={picture}
                    style={{ width: '100%' }}
                    placeholderStyle={{ height: placeHolderSize }}
                >
                    <Responsive
                        as={React.Fragment}
                        minWidth={
                            blockResponsive
                                ? configUI.responsive.mobile.minWidth
                                : configUI.responsive.computer.minWidth
                        }
                    >
                        <OuterWrapper horizontalPosition={horizontalPosition} verticalPosition={verticalPosition}>
                            <WrapperContenu color={color} justification={justification} width={width || 'moyen'}>
                                <div className={styles['inner-wrapper']}>
                                    <LegendBloc {...legendBlocProps} />
                                </div>
                            </WrapperContenu>
                        </OuterWrapper>
                    </Responsive>
                </CMSImageContainer>
            </ClickableContainer>
            {!blockResponsive && (
                <Responsive as={React.Fragment} maxWidth={configUI.responsive.tablet.maxWidth}>
                    <LegendBloc {...legendBlocProps} />
                </Responsive>
            )}
        </div>
    );
});

// eslint-disable-next-line react/prop-types
export const TitleBlocLoader: React.FC<{
    size?: PlaceholderSize;
    compactResponsive?: boolean;
    largeLegend?: boolean;
}> = React.memo(({ size, compactResponsive, largeLegend }) => {
    return (
        <>
            <Placeholder className="full-width" style={{ height: convertPlaceholderSize(size, compactResponsive) }}>
                <Placeholder.Image className="full-width" />
            </Placeholder>
            <Responsive as={React.Fragment} maxWidth={configUI.responsive.tablet.maxWidth}>
                <div className="full-width">
                    <LegendBlocLoader large={largeLegend} />
                </div>
            </Responsive>
        </>
    );
});
